import RocketShip from '@/assets/images/rocket-ship.png';
import { Button, FullscreenDialog, Text, VSpacer } from '@/components/DesignSystem';
import { LocalStorageUtility } from '@/utilities/LocalStorageUtility';
import { ApiRewardsProgram } from '@api/interfaces/ApiRewardsProgram';
import { Stack } from '@mui/material';
import { daysFromDate, getJsDateFromString } from '@shared/utilities';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface NewProgramWelcomeModalProps {
  program: ApiRewardsProgram,
}

const NewProgramLocalStorageKey = 'new-program-modal-dismissed';

export const NewProgramWelcomeModal = ({
  program,
}: NewProgramWelcomeModalProps) => {
  const { t } = useTranslation();
  const [shouldShow, setShouldShow] = useState(false);

  const programStartDate = getJsDateFromString(program.startDate);
  const programEndDate = getJsDateFromString(program.endDate, { endOfDay: true });

  useEffect(() => {
    const currentDate = new Date();
    const daysSinceProgramStart = Math.floor(daysFromDate(programStartDate));

    const newProgramStarted = (
      currentDate >= programStartDate
      && currentDate <= programEndDate
      && daysSinceProgramStart <= 30
    );

    if (!newProgramStarted) {
      return;
    }

    const dismissed = LocalStorageUtility.getItem(NewProgramLocalStorageKey);
    if (dismissed) {
      const dismissedDate = getJsDateFromString(dismissed);
      const isStillInProgramPeriod =
        dismissedDate >= programStartDate && dismissedDate <= programEndDate;
      if (isStillInProgramPeriod) {
        return;
      }
    }

    setShouldShow(true);
  }, [program]);

  const handleDismiss = () => {
    LocalStorageUtility.set(NewProgramLocalStorageKey, new Date().toISOString());
    setShouldShow(false);
  };

  if (!shouldShow) {
    return null;
  }

  return (
    <FullscreenDialog hideHeader open testID="new-program-modal">
      <Stack alignItems="center" height="100vh" justifyContent="center" textAlign="center">
        <img src={RocketShip} />
        <VSpacer size="10" />
        <Text category="headline-small" sx={{ fontSize: '25px' }}>
          {t($ => $.customerDashboard.welcome.newPeriod, { programName: program.name })}
        </Text>
        <VSpacer size="4" />
        <Text category="body-medium">
          {t(
            $ => $.customerDashboard.welcome.dates,
            { startDate: programStartDate, endDate: programEndDate },
          )}
        </Text>
        <VSpacer size="10" />
        <Button onClick={handleDismiss} testID="new-program-lets-go-button">
          {t($ => $.customerDashboard.welcome.letsGo)}
        </Button>
      </Stack>
    </FullscreenDialog>
  );
};
